import { z } from 'zod';
import { AUDIT_ACTIONS } from '../enums';
import { listQuery, ymd } from './common';

/**
 * Known settings and their validators. Unknown keys are rejected so the
 * settings table cannot accumulate typos.
 */
export const SETTING_DEFINITIONS = {
  'company.name': {
    schema: z.string().trim().min(1).max(200),
    default: 'My Company',
    description: 'Company name shown in the header and on exports',
  },
  'reports.weekStartsOn': {
    schema: z.union([z.literal(0), z.literal(1)]),
    default: 1,
    description: 'First day of the week for "This Week" / "Last Week" (0 = Sunday, 1 = Monday)',
  },
  'marketing.accountDisplayMode': {
    schema: z.enum(['ACCOUNT_ID', 'ACCOUNT_NAME', 'BOTH']),
    default: 'BOTH',
    description:
      'How Google Ads / advertising accounts are shown: account ID, account name, or both',
  },
  'security.idleLockMinutes': {
    schema: z.number().int().min(1).max(240),
    default: 15,
    description:
      'Minutes without activity before the app locks and asks for the PIN or fingerprint / Face ID',
  },
  'reports.defaultYearMode': {
    schema: z.enum(['CALENDAR', 'FINANCIAL']),
    default: 'CALENDAR',
    description:
      'Default year for "This Year" and yearly reports (calendar or Apr–Mar financial year)',
  },
} as const;

export type SettingKey = keyof typeof SETTING_DEFINITIONS;
export const SETTING_KEYS = Object.keys(SETTING_DEFINITIONS) as SettingKey[];

export const settingKeySchema = z.enum(SETTING_KEYS as [SettingKey, ...SettingKey[]]);
export const settingUpdateSchema = z.object({ value: z.unknown() });

export const auditLogListSchema = listQuery.extend({
  entity: z.string().trim().max(60).optional(),
  entityId: z.string().trim().max(36).optional(),
  userId: z.uuid().optional(),
  action: z.enum(AUDIT_ACTIONS).optional(),
  from: ymd.optional(),
  to: ymd.optional(),
});
